'use client'

import type { CardRouteBadgeData } from '@/features/card/utils/cardSolverRouteMatching'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { cn } from '@/ui/utils/style'
import { useId } from 'react'

interface CardRouteBadgeProps {
  badge: CardRouteBadgeData | null
  className?: string
}

function getModeLabel(solverType: string): 'Borrow' | 'Spend' {
  return solverType.endsWith('-withdraw') ? 'Spend' : 'Borrow'
}

function getModeText(badge: CardRouteBadgeData): string {
  const modes = Array.from(new Set(badge.cards.map((card) => getModeLabel(card.solverType))))
  if (modes.length === 0) return ''
  if (modes.length === 1) return `${modes[0]} mode`
  return `${modes.join(' / ')} modes`
}

function getTooltipText(badge: CardRouteBadgeData): string {
  const modeText = getModeText(badge)
  const suffix = modeText ? ` • ${modeText}` : ''

  if (badge.count > 1) {
    return `Used by ${badge.count} cards${suffix}`
  }

  const card = badge.cards[0]
  if (!card) return `Used by card${suffix}`

  const identity = [card.label?.trim(), card.last4 ? `•••• ${card.last4}` : undefined].filter(Boolean).join(' ')
  return identity ? `Used by card ${identity}${suffix}` : `Used by card${suffix}`
}

export function CardRouteBadge({ badge, className }: CardRouteBadgeProps) {
  const gradientId = useId()

  if (!badge) return null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex h-5 w-5 shrink-0 items-center justify-center',
            className,
          )}
          aria-label={getTooltipText(badge)}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px] fill-none">
            <defs>
              <linearGradient id={gradientId} x1="2" x2="22" y1="5" y2="19" gradientUnits="userSpaceOnUse">
                <stop stopColor="oklch(var(--gradient-yellow))" />
                <stop offset="1" stopColor="oklch(var(--gradient-green))" />
              </linearGradient>
            </defs>
            <rect x="2" y="5" width="20" height="14" rx="2" stroke={`url(#${gradientId})`} strokeWidth="2" />
            <path d="M2 10h20" stroke={`url(#${gradientId})`} strokeWidth="2" />
          </svg>
        </span>
      </TooltipTrigger>
      <TooltipContentShort>{getTooltipText(badge)}</TooltipContentShort>
    </Tooltip>
  )
}
